import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { supabase } from "@/integrations/supabase/client";
import { useCategoryTree } from "@/hooks/use-category-tree";

export const Route = createFileRoute("/marketplace/category/$slug")({
  component: ProductCategoryPage,
  head: ({ params }) => ({
    meta: [
      { title: `${params.slug} — Marketplace` },
      { name: "description", content: `Products in the ${params.slug} category.` },
    ],
  }),
});

function ProductCategoryPage() {
  const { slug } = Route.useParams();
  const { data: tree, isLoading: catLoading } = useCategoryTree("categories", slug);
  const current = tree?.current;
  const ids = current ? [current.id, ...tree!.descendants.map((d) => d.id)] : [];

  const { data: products, isLoading } = useQuery({
    queryKey: ["public", "products", "by-cats", ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id,kind,name,slug,description,cover_url,category_id,featured")
        .eq("active", true)
        .in("category_id", ids)
        .order("featured", { ascending: false })
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  if (!catLoading && !current) throw notFound();

  return (
    <PageShell>
      <PageHero
        eyebrow={tree?.ancestors.map((a) => a.name).join(" › ") || "Marketplace"}
        title={current?.name ?? slug}
        sub={current?.description ?? "Browse all products in this category."}
      />
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <nav className="mb-6 text-sm text-muted-foreground">
            <Link to="/marketplace" className="hover:text-foreground">Marketplace</Link>
            {tree?.ancestors.map((a) => (
              <span key={a.id}>
                {" › "}
                <Link to="/marketplace/category/$slug" params={{ slug: a.slug }} className="hover:text-foreground">{a.name}</Link>
              </span>
            ))}
            <span> › <span className="text-foreground">{current?.name}</span></span>
          </nav>

          {tree && tree.children.length > 0 && (
            <div className="mb-8 flex flex-wrap gap-2">
              {tree.children.map((c) => (
                <Link key={c.id} to="/marketplace/category/$slug" params={{ slug: c.slug }} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm hover:border-primary/40">
                  {c.name}
                </Link>
              ))}
            </div>
          )}

          {(isLoading || catLoading) && <p className="text-center text-muted-foreground">Loading…</p>}
          {!isLoading && !products?.length && <p className="text-center text-muted-foreground">No products in this category yet.</p>}

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {products?.map((p: any, i: number) => (
              <Link
                key={p.id}
                to="/p/$slug"
                params={{ slug: p.slug }}
                className="group block overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-glow focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {p.cover_url ? (
                  <img src={p.cover_url} alt={p.name} className="aspect-[16/10] w-full object-cover transition duration-500 group-hover:scale-[1.03]" loading="lazy" />
                ) : (
                  <div className={`aspect-[16/10] bg-gradient-to-br ${["from-violet-500 to-fuchsia-500","from-sky-500 to-cyan-500","from-emerald-500 to-teal-500","from-amber-500 to-orange-500","from-pink-500 to-rose-500","from-indigo-500 to-blue-500"][i%6]}`} />
                )}
                <div className="p-5">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{p.kind}</div>
                  <h3 className="mt-1 text-base font-semibold group-hover:text-primary">{p.name}</h3>
                  {p.description && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.description}</p>}
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary opacity-0 transition group-hover:opacity-100">
                    View details →
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
